import { inject, injectable } from 'inversify'
import { jsonDateParser } from 'json-date-parser'
import { LRUCache } from 'lru-cache'

import { InjectionTokens } from '@/controller/tokens'

import { UserWaypointChanged } from '@/domain/events/UserWaypointChanged'
import { ADHP, AwWaypoint, City, CoordWaypoint, VisualPosition, Waypoint } from '@/domain/models/Waypoint'
import { WaypointTypeCode } from '@/domain/models/WaypointType/WaypointType'
import { Coordinates } from '@/domain/protocols/Coordinates'
import { Result } from '@/domain/protocols/Result'
import { DatasetFreshnessStateMutator } from '@/domain/states/DatasetFreshnessState'
import { LoadUserWaypointsUseCase } from '@/domain/useCases/UserWaypoints/loadWaypoints'

import type { INavaidRepository } from '@/data/NavaidRepository/INavaidRepository'

import { adhpQuery } from './adhpQuery'
import { AeroInfoMapper } from './AeroInfoMapper'
import { AdhpQueryResponse, IAeroInfoRepository, WaypointsFullResponseItem } from './IAeroInfoRepository'
import { trimWaypointsFull } from './trimWaypointsFull'

import { DatasetCache } from '@/infra/datasetCache/DatasetCache'
import { DATASET_POLICIES } from '@/infra/datasetCache/datasetPolicies'
import type { IGeocodeApi } from '@/infra/Geocode/IGeocodeApi'
import { AERODATA_API_BASE } from '@/infra/http/apiBaseUrls'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import type { ILogger } from '@/infra/logger/ILogger'
import {
  coordinateFromRadialDistance,
  DMSToDecimal,
  nmDistance,
  nmPythagoreanDistance,
  parseUserCoordinateInput
} from '@/utils/coordinates'
import { removeAccents } from '@/utils/string'
import { isSet } from '@/utils/validation'

@injectable()
export class AeroInfoRepository implements IAeroInfoRepository {
  private ADHPs: ADHP[] = []
  /**
   * Detalhe completo por ICAO (com `rotaerHTML`, `infotemp`, `aipsup`, METAR/TAF...), vindo do refresh
   * por aerodromo.
   *
   * Fica FORA da lista residente de proposito: esses campos somam 15,19 MB no payload completo, e
   * injeta-los na lista faria uma sessao longa acumular megabytes de HTML — exatamente o que o trim do
   * `waypoints_full` foi feito para evitar. O LRU limita o acumulo aos ultimos aerodromos visitados.
   */
  private fullADHPs = new LRUCache<string, ADHP>({ max: 30 })
  private AwWaypoints: AwWaypoint[] = []
  private Cities: City[] = []
  private VisualPositions: VisualPosition[] = []
  private UserWaypoints: CoordWaypoint[] = []
  private lastLoadedAt: Date | null = null
  private refreshPromise: Promise<Result<void>> | null = null
  private adhpLastLoadedAt: Map<string, Date> = new Map()
  private adhpRefreshPromises: Map<string, Promise<Result<ADHP | null>>> = new Map()

  public constructor(
    @inject(InjectionTokens.HttpClient) private remoteClient: IHttpClient,
    @inject(InjectionTokens.GeocodeApi) private geocodeApi: IGeocodeApi,
    @inject(InjectionTokens.Logger) private logger: ILogger,
    @inject(InjectionTokens.NavaidRepository) private navaidRepository: INavaidRepository,
    @inject(InjectionTokens.DatasetCache) private datasetCache: DatasetCache
  ) {
    // Os pontos do usuário aparecem na busca (`waypoints`) e na rota. Quando são criados,
    // editados, importados ou removidos, recarregamos só essa fatia do cache (sem rebaixar
    // todo o waypoints_full), mantendo a busca em dia sem depender de um init() completo.
    UserWaypointChanged.subscribe(() => {
      void this._loadCustomWaypoints()
    })
  }

  private get fixedWaypoints(): Waypoint[] {
    return [...this.ADHPs, ...this.AwWaypoints, ...this.Cities, ...this.VisualPositions]
  }

  public get waypoints(): Waypoint[] {
    return [...this.fixedWaypoints, ...this.UserWaypoints]
  }

  protected getRemoteUrl(): string {
    return AERODATA_API_BASE
  }

  public getAllData() {
    return this.waypoints
  }

  protected getGraphQlUrl(): string {
    return `${this.getRemoteUrl()}/graphql`
  }

  public async init(): Promise<Result<void>> {
    try {
      await this._loadWaypoints()
      await this._loadCustomWaypoints()
    } catch (error) {
      this.logger.error('AeroInfoRepository', `Error loading waypoints: ${error}`)
    }

    return Result.ok()
  }

  private get _waypointsFullUrl(): string {
    return `${this.getRemoteUrl()}/waypoints_full`
  }

  /**
   * Carrega o `waypoints_full` com cache local e TTL.
   *
   * O que e gravado no cache e o payload **ja enxuto** (`trimWaypointsFull`), nao o original: o
   * original tem 24,95 MB e 61% disso e detalhe de ADHP que so a tela de aerodromo usa e que ela
   * busca por ICAO. Gravar o enxuto e o que faz o reload valer a pena — sem o trim, estariamos
   * persistindo 25 MB para reparsear tudo de novo.
   */
  private async _loadWaypoints(): Promise<Result<void>> {
    const cached = await this.datasetCache.read('waypoints')

    // Só a entrada fresca evita a rede; a stale vai à rede e fica de reserva (ver `loadDatasetBytes`).
    if (cached && cached.freshness === 'fresh' && cached.kind === 'text') {
      try {
        const items = JSON.parse(cached.payload as string) as WaypointsFullResponseItem[]

        if (items.length >= DATASET_POLICIES.waypoints.minExpectedItems) {
          this._mapRemoteToLocal(items)
          this.lastLoadedAt = new Date(cached.meta.fetchedAt)
          DatasetFreshnessStateMutator.report('waypoints', { loadedAt: this.lastLoadedAt, isDegraded: false })

          return Result.ok()
        }

        this.logger.warn('AeroInfoRepository', `Cache com ${items.length} itens; indo a rede`)
      } catch (error: any) {
        this.logger.warn('AeroInfoRepository', `Cache ilegivel, indo a rede: ${error?.message}`)
      }
    }

    return this._loadWaypointsFromNetwork()
  }

  private async _loadWaypointsFromNetwork(): Promise<Result<void>> {
    try {
      const response = await this.remoteClient.get<WaypointsFullResponseItem[]>(this._waypointsFullUrl, {
        timeout: HTTP_TIMEOUTS.dataset
      })

      // O cliente HTTP devolve `success: false` em falha de rede, sem lançar: o fallback precisa estar
      // aqui também, e não só no `catch`.
      if (response.success === false) {
        return this._loadCachedWaypointsFallback(response.error)
      }

      const trimmed = trimWaypointsFull(response.response)

      this._mapRemoteToLocal(trimmed.items)
      this.lastLoadedAt = new Date()
      DatasetFreshnessStateMutator.report('waypoints', { loadedAt: this.lastLoadedAt, isDegraded: false })

      // Gravado DEPOIS do mapeamento: se o parse/mapeamento falhar, nao persistimos lixo. E o
      // `void` e deliberado — a gravacao nao precisa gatear o boot.
      void this.datasetCache.write('waypoints', JSON.stringify(trimmed.items), {
        sourceStamp: trimmed.sourceStamp,
        itemCount: trimmed.itemCount
      })

      return Result.ok()
    } catch (error: any) {
      return this._loadCachedWaypointsFallback(error)
    }
  }

  /**
   * Ultimo recurso: a rede falhou e existe copia local, stale ou VENCIDA.
   *
   * Usar e melhor que deixar o app sem dado aeronautico nenhum — desde que, se ela estiver vencida, o
   * usuario saiba. O estado degradado e o que o banner global le para avisar "dados de HH:MM, nao foi
   * possivel atualizar". Copia stale ainda esta dentro do hard TTL e e usada sem aviso.
   */
  private async _loadCachedWaypointsFallback(networkError: any): Promise<Result<void>> {
    const cached = await this.datasetCache.read('waypoints')

    if (!cached || cached.kind !== 'text') {
      return Result.fail(networkError?.message ?? 'Could not fetch waypoints dataset')
    }

    try {
      const items = JSON.parse(cached.payload as string) as WaypointsFullResponseItem[]

      if (items.length < DATASET_POLICIES.waypoints.minExpectedItems) {
        return Result.fail(networkError?.message ?? 'Could not fetch waypoints dataset')
      }

      const isDegraded = cached.freshness === 'expired'

      this._mapRemoteToLocal(items)
      this.lastLoadedAt = new Date(cached.meta.fetchedAt)
      DatasetFreshnessStateMutator.report('waypoints', { loadedAt: this.lastLoadedAt, isDegraded })
      this.logger.warn(
        'AeroInfoRepository',
        `Usando copia ${isDegraded ? 'vencida' : 'local'} do waypoints_full (rede indisponivel)`
      )

      return Result.ok()
    } catch {
      return Result.fail(networkError?.message ?? 'Could not fetch waypoints dataset')
    }
  }

  public getLastLoadedAt(): Date | null {
    return this.lastLoadedAt ? new Date(this.lastLoadedAt) : null
  }

  public getADHPLastLoadedAt(code: string): Date | null {
    const normalizedCode = code?.trim()?.toUpperCase()

    if (!normalizedCode) {
      return null
    }

    const adhpDate = this.adhpLastLoadedAt.get(normalizedCode)
    return adhpDate ? new Date(adhpDate) : null
  }

  public async refreshWaypoints(): Promise<Result<void>> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this._loadWaypoints()
      .then((result) => result)
      .catch((error) => {
        this.logger.error('AeroInfoRepository', `Error refreshing waypoints: ${error}`)
        return Result.fail<void>(error)
      })
      .finally(() => {
        this.refreshPromise = null
      })

    return this.refreshPromise
  }

  public async refreshADHPData(code: string): Promise<Result<ADHP | null>> {
    const normalizedCode = code?.trim()?.toUpperCase()

    if (!normalizedCode) {
      return Result.fail('Invalid ADHP code')
    }

    const existingPromise = this.adhpRefreshPromises.get(normalizedCode)
    if (existingPromise) {
      return existingPromise
    }

    const refreshPromise = this._refreshSingleADHP(normalizedCode)
      .catch((error) => {
        this.logger.error('AeroInfoRepository', `Error refreshing ADHP ${normalizedCode}: ${error}`)
        return Result.fail<ADHP | null>(error)
      })
      .finally(() => {
        this.adhpRefreshPromises.delete(normalizedCode)
      })

    this.adhpRefreshPromises.set(normalizedCode, refreshPromise)

    return refreshPromise
  }

  protected _mapRemoteToLocal(remoteData: WaypointsFullResponseItem[]): void {
    const { ADHPs, AwWaypoints, Cities, VisualPositions } = AeroInfoMapper(remoteData)

    this.ADHPs = ADHPs
    this.AwWaypoints = AwWaypoints
    this.Cities = Cities
    this.VisualPositions = VisualPositions
  }

  /*
   * `_seedADHPLoadDates` foi REMOVIDO.
   *
   * Ele semeava `adhpLastLoadedAt` de todos os 6.071 ADHPs com o timestamp do `waypoints_full`, o que
   * era verdade enquanto o payload trazia o detalhe completo. Depois do trim isso passaria a afirmar
   * que o ROTAER/METAR de cada aerodromo foi carregado no boot — quando ele nem existe em memoria.
   *
   * Agora `getADHPLastLoadedAt(code)` significa o que o nome diz: quando o DETALHE daquele aerodromo
   * foi buscado. Enquanto nao houver busca por ICAO devolve `null`, e quem le cai no
   * `getLastLoadedAt()` (a data do dado enxuto), que e honesto para nome/cidade/pista.
   */

  private async _refreshSingleADHP(code: string): Promise<Result<ADHP | null>> {
    const dedicatedResult = await this._fetchADHPByCode(code)

    if (dedicatedResult.isSuccess) {
      const refreshedAdhp = dedicatedResult.getValue()

      if (!refreshedAdhp) {
        return Result.ok(null)
      }

      this._upsertADHP(refreshedAdhp)
      this.adhpLastLoadedAt.set(code, new Date())

      return Result.ok(refreshedAdhp.clone())
    }

    /*
     * O fallback para o `waypoints_full` foi REMOVIDO.
     *
     * Ele existia porque o payload do boot trazia o detalhe do ADHP (ROTAER, METAR, INFOTEMP...), logo
     * recarregar a lista era de fato uma segunda chance de obter o detalhe. Depois do trim o payload
     * NAO tem mais esses campos, e o fallback passou a fazer tres coisas erradas de uma vez:
     *
     * 1. devolvia `Result.ok(adhpEnxuto)` — convertia uma FALHA em falso sucesso, entao a tela
     *    exibia "nao ha ROTAER disponivel" em vez de um erro;
     * 2. estampava `adhpLastLoadedAt = agora`, afirmando que o detalhe acabou de ser carregado
     *    (a tela mostrava "Atualizado agora" sem ter o dado);
     * 3. rebaixava os 8 MB do `waypoints_full` para nada.
     *
     * Agora a falha se propaga. O ADHP enxuto continua disponivel para o cabecalho via `getADHPData`,
     * que e o `initialData` da query — o que se perde e apenas a mentira.
     */
    this.logger.error('AeroInfoRepository', `Dedicated ADHP refresh failed for ${code}: ${dedicatedResult.error}`)

    return Result.fail(dedicatedResult.error)
  }

  private async _fetchADHPByCode(code: string): Promise<Result<ADHP | null>> {
    const response = await this.remoteClient.post<{ data?: AdhpQueryResponse }>(
      this.getGraphQlUrl(),
      {
        query: adhpQuery(code),
        variables: null
      },
      {
        // Consulta GraphQL: POST que e leitura, logo retentar e seguro.
        idempotent: true,
        /*
         * Classe `dataset` (60 s), NAO o default `interactive` (15 s).
         *
         * Medido no endpoint real: a latencia desta query e bimodal e depende do aerodromo — ~1 s para
         * a maioria (SBBR, SBPA) e **21-24 s** para outros (SBGR, SBRJ), identico pelo proxy e direto,
         * logo e da API. Com 15 s o app abortava, o retry idempotente repetia 3x (45 s no total) e a
         * tela terminava exibindo "nao ha ROTAER disponivel" para um aerodromo que TEM ROTAER.
         *
         * Isto passou a ser critico com o trim do `waypoints_full`: antes o detalhe vinha no payload do
         * boot e esta chamada era so revalidacao em background, entao o timeout curto nao aparecia.
         */
        timeout: HTTP_TIMEOUTS.dataset,
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )

    if (!response.success || 'error' in response) {
      return Result.fail('Network error while fetching ADHP data')
    }

    if (response.status >= 400) {
      return Result.fail(`HTTP ${response.status}`)
    }

    const rawAdhp = response.response?.data?.ADHP

    if (!rawAdhp) {
      return Result.ok(null)
    }

    const sanitizedDatesResponse = JSON.parse(
      JSON.stringify(response.response.data),
      jsonDateParser
    ) as AdhpQueryResponse
    const adhp = this._mapADHPQueryResponseToModel(sanitizedDatesResponse.ADHP)

    return Result.ok(adhp)
  }

  private _mapADHPQueryResponseToModel(data: AdhpQueryResponse['ADHP']): ADHP {
    return ADHP.create(
      {
        code: data.code,
        name: data.name,
        coordinates: Coordinates.create({
          latitude: data.geolocation.lat,
          longitude: data.geolocation.lng,
          altitude: data.geolocation.alt
        }),
        createdAt: new Date(data.createdAt),
        updatedAt: new Date(data.updatedAt)
      },
      {
        type: data.type,
        city: data.city,
        uf: data.uf,
        rotaer: data.rotaer,
        rotaerHTML: data.rotaerHTML,
        infotemp: data.infotemp,
        aipsup: data.aipsup,
        notam: data.notam,
        meteorology: data.meteorology,
        metarHistory: data.metarHistory,
        daytime: data.daytime,
        charts: data.charts
      }
    )
  }

  private _upsertADHP(adhp: ADHP): void {
    const normalizedCode = adhp.code?.trim()?.toUpperCase()

    if (!normalizedCode) {
      return
    }

    const liteAdhp = ADHP.create(
      {
        code: adhp.code,
        name: adhp.name,
        coordinates: adhp.coordinates,
        createdAt: adhp.createdAt,
        updatedAt: adhp.updatedAt
      },
      {
        type: adhp.type,
        city: adhp.city,
        uf: adhp.uf,
        // `rotaer` faz parte do dado enxuto: e dele que a camada de pontos deriva os filtros.
        rotaer: adhp.rotaer
      }
    )

    const updateList = (list: ADHP[], nextValue: ADHP) => {
      const existingIndex = list.findIndex((candidate) => candidate.code?.trim()?.toUpperCase() === normalizedCode)

      if (existingIndex >= 0) {
        list[existingIndex] = nextValue
        return
      }

      list.push(nextValue)
    }

    this.fullADHPs.set(normalizedCode, adhp)
    updateList(this.ADHPs, liteAdhp)
  }

  private async _loadCustomWaypoints() {
    const userWaypoints = await new LoadUserWaypointsUseCase().execute()

    if (userWaypoints.isSuccess) {
      this.UserWaypoints = userWaypoints.getValue()
    }
  }

  getExactMatchWaypoint(waypoint: Waypoint): Waypoint | null {
    /*
    Verificar pelo código;
    Se tiver apenas um, devolve.
    Senão, verificar pelo name.
    Se agora tiver apenas um, devolve.
    Senão, verifica qual waypoint do aeroinfo
    está mais próximo do ponto da rota importada.
    */

    const getByCode = () => {
      const queryResult = this.fixedWaypoints.filter((w) => {
        const hasCode = isSet(w.code)
        const areCodesEqual = w.code === waypoint.code

        return hasCode ? areCodesEqual : false
      })

      return queryResult
    }

    const getByName = (waypoints: Waypoint[]) => {
      const queryResult = waypoints.filter((w) => {
        const hasName = isSet(w.name) && isSet(waypoint.name)
        if (!hasName) return false
        return w.name === waypoint.name
      })

      return queryResult
    }

    const getByProximity = (waypoints: Waypoint[]) => {
      if (waypoints.length === 0) return null
      let closestWaypoint: Waypoint | null = null
      let closestDistance = Number.MAX_VALUE

      for (let i = 0; i < waypoints.length; i++) {
        try {
          const w = waypoints[i]
          if (!w || !w.coordinates) {
            continue
          }
          const distance = nmDistance(w.coordinates, waypoint.coordinates)
          if (distance < closestDistance) {
            closestDistance = distance
            closestWaypoint = w
          }
        } catch (e) {
          console.error(`Erro na iteração ${i}. Objeto waypoint:`, waypoints[i], 'Erro:', e)
        }
      }
      return closestWaypoint
    }

    let getByCodeResult: Waypoint[] = []
    let getByNameResult: Waypoint[] = []
    let getByProximityResult: Waypoint

    getByCodeResult = getByCode()
    if (getByCodeResult.length === 1) {
      return getByCodeResult[0].clone()
    } else if (getByCodeResult.length === 0) {
      getByNameResult = getByName(this.fixedWaypoints)
    } else if (getByCodeResult.length > 1) {
      getByNameResult = getByName(getByCodeResult)
    }
    if (getByNameResult.length === 1) {
      return getByNameResult[0].clone()
    } else if (getByNameResult.length > 1) {
      getByProximityResult = getByProximity(getByNameResult)
    }

    return getByProximityResult?.clone() ?? waypoint
  }

  searchByProximity(center: Coordinates, radius: number, max?: number, typeCode?: WaypointTypeCode): Waypoint[] {
    interface FindItem {
      item: Waypoint
      distance: number
    }

    const priorityMap: any = {
      Navaid: 2,
      VisualPosition: 2,
      AwWaypoint: 1,
      City: 1
    }

    const getPriorityMap = (wpt: Waypoint) => {
      if (wpt.waypointType?.codeName === 'ADHP') {
        if ((wpt as ADHP).type === 'AD') return 4
        else return 2
      }

      return priorityMap[wpt.waypointType?.codeName!]
    }

    let mapItems = this.waypoints
      .map((wpt) => {
        return {
          item: wpt,
          distance: nmDistance(center, wpt.coordinates)
        } as FindItem
      })
      .filter((item) => item.distance <= radius)
      .sort((a, b) => a.distance / getPriorityMap(a.item) - b.distance / getPriorityMap(b.item))

    if (typeCode) {
      mapItems = mapItems.filter((item) => item.item.constructor.name === typeCode)
    }

    if (max) {
      mapItems = mapItems.slice(0, max)
    }

    return mapItems.map((search) => search.item)
  }
  searchByProximityWithLowPrecision(
    center: Coordinates,
    radius: number,
    max?: number,
    typeCode?: WaypointTypeCode
  ): Waypoint[] {
    interface FindItem {
      item: Waypoint
      distance: number
    }

    const priorityMap: any = {
      Navaid: 2,
      VisualPosition: 2,
      AwWaypoint: 1,
      City: 1
    }

    const getPriorityMap = (wpt: Waypoint) => {
      if (wpt.waypointType?.codeName === 'ADHP') {
        if ((wpt as ADHP).type === 'AD') return 4
        else return 2
      }

      return priorityMap[wpt.waypointType?.codeName!]
    }

    let mapItems = this.waypoints
      .map((wpt) => {
        return {
          item: wpt,
          distance: nmPythagoreanDistance(center, wpt.coordinates)
        } as FindItem
      })
      .filter((item) => item.distance <= radius)
      .sort((a, b) => a.distance / getPriorityMap(a.item) - b.distance / getPriorityMap(b.item))

    if (typeCode) {
      mapItems = mapItems.filter((item) => item.item.constructor.name === typeCode)
    }

    if (max) {
      mapItems = mapItems.slice(0, max)
    }

    return mapItems.map((search) => search.item)
  }

  /**
   * Coordenadas de um navaid (VOR/NDB) por código exato, usadas como referência para
   * criação de ponto por radial/distância. Retorna null quando não há match exato.
   */
  private _findNavaidCoordinatesByCode(code: string): Coordinates | null {
    const normalizedCode = code?.trim().toUpperCase()
    if (!normalizedCode) return null

    const [match] = this.navaidRepository.searchByInfo(normalizedCode, 1)

    return match && match.code?.toUpperCase() === normalizedCode ? match.coordinates : null
  }

  findInPlace(coord: Coordinates, typeCode: WaypointTypeCode = 'ADHP', radius: number = 0.3): Waypoint {
    const search = this.searchByProximity(coord, radius, 1, typeCode)

    if (search.length === 0) {
      return null
    }

    return search[0]
  }

  async searchByInfo(info: string, max?: number, adhpOnly?: boolean): Promise<Waypoint[]> {
    if (!info) return []

    const normalizedInfo = removeAccents(info).toLowerCase().trim()
    if (!normalizedInfo) return []

    if (!adhpOnly) {
      const coordinates = parseUserCoordinateInput(info)
      if (coordinates) {
        return [CoordWaypoint.create({ coordinates }, {})]
      }
    }

    // Keep partial parsing only in search, to preserve incremental coordinate lookup.
    const latRE = /^([0-9]{1,2})\s*[º°]?\s*([0-5]?\d)?\s*["']?\s*([0-5]?\d)?\s*["']?\s*([NnSs])?\s*\/?,?-?\s*$/gi
    const latSearch = latRE.exec(info)
    if (latSearch && !adhpOnly) {
      const coordinates = DMSToDecimal(latSearch)
      return [CoordWaypoint.create({ coordinates }, {})]
    }

    const partialDecimalCoordRE = /^\(?\s*[+-]?\d+(?:[.,]\d+)?\s*\)?$/
    const partialMatch = partialDecimalCoordRE.exec(info.trim())

    if (!adhpOnly && partialMatch) {
      const lat = parseFloat(info.trim().replace(',', '.').replace(/[()]/g, ''))

      if (lat >= -90 && lat <= 90) {
        const coordinates = Coordinates.create({ latitude: lat, longitude: 0 })
        return [CoordWaypoint.create({ coordinates }, {})]
      }
    }

    const isRadialDistancePattern =
      !adhpOnly && info.trim().length >= 8 && /^[A-Za-z][A-Za-z0-9 ]*\/?(\d{6})$/.test(info.trim())

    if (isRadialDistancePattern) {
      const trimmed = info.trim().toUpperCase().replace('/', '')
      const prefix = trimmed.slice(0, -6)
      const radialStr = trimmed.slice(-6, -3)
      const distStr = trimmed.slice(-3)
      const radial = parseInt(radialStr, 10)
      const distance = parseInt(distStr, 10)

      if (radial >= 1 && radial <= 360) {
        // O ponto de referência costuma ser um auxílio à navegação (VOR/NDB). Após a migração
        // dos navaids para o dataset FlatRecord (NavaidRepository), eles deixaram de vir no
        // `waypoints_full`, então buscamos primeiro em `this.waypoints` e caímos no NavaidRepository
        // quando não encontrado (ex.: "SVD290035" — PLA-217).
        const refWaypoint = this.waypoints.find((w) => w.code?.toUpperCase() === prefix)
        const refCoordinates = refWaypoint?.coordinates ?? this._findNavaidCoordinatesByCode(prefix)

        if (refCoordinates) {
          const coordinates = coordinateFromRadialDistance(refCoordinates, radial, distance)
          const customName = `${prefix} R${radialStr} D${distStr}`

          const resultWaypoints = [CoordWaypoint.create({ coordinates, customName }, {})]

          return resultWaypoints
        }
      }

      return []
    }

    const candidates = adhpOnly ? this.ADHPs : this.waypoints
    const maxResults = max || 10
    const earlyStopThreshold = maxResults * 2

    // Precompute the normalized info for scoring
    // For performance, define a scoring function once
    const scoreWaypoint = (waypoint: Waypoint): number | null => {
      const normalizedCode = waypoint.code?.toLowerCase() ?? null
      const normalizedName = waypoint.name ? removeAccents(waypoint.name.toLowerCase()).split(',')[0].trim() : null
      const normalizedCustomName = waypoint.customName ? removeAccents(waypoint.customName.toLowerCase()) : null
      const normalizedCity = waypoint.extra?.city ? removeAccents(waypoint.extra.city.toLowerCase()).trim() : null

      const isInfoInName = normalizedName?.includes(normalizedInfo)
      const isInfoInCustomName = normalizedCustomName?.includes(normalizedInfo)
      const isInfoInCity = normalizedCity?.includes(normalizedInfo)

      // Scoring logic (mirroring the original logic):
      // The logic below returns a score depending on which conditions are met.
      // Higher score means better match.

      // ADHP exact code
      if (waypoint instanceof ADHP && normalizedCode === normalizedInfo) return 50

      // AWWaypoint exact code
      if (waypoint instanceof AwWaypoint && normalizedCode === normalizedInfo) return 45

      // CoordWaypoint custom name exact or starts with
      if (waypoint instanceof CoordWaypoint && (isInfoInCustomName || normalizedCustomName?.startsWith(normalizedInfo)))
        return 42

      // VisualPosition name exact or starts with
      if (waypoint instanceof VisualPosition && (isInfoInName || normalizedName?.startsWith(normalizedInfo))) return 40

      // ADHP city exact match
      if (normalizedCity === normalizedInfo && waypoint instanceof ADHP) {
        if (waypoint.extra.type === 'AD' && waypoint.code?.startsWith('SB')) return 35
        if (waypoint.extra.type === 'AD') return 25
        if (waypoint.extra.type === 'HP') return 15
      }

      // ADHP city partial (info in city)
      if (isInfoInCity && waypoint instanceof ADHP) {
        if (waypoint.extra.type === 'AD' && waypoint.code?.startsWith('SB')) return 34
        if (waypoint.extra.type === 'AD') return 24
        if (waypoint.extra.type === 'HP') return 14
      }

      // ADHP name exact match
      if (normalizedName === normalizedInfo && waypoint instanceof ADHP) {
        if (waypoint.extra.type === 'AD' && waypoint.code?.startsWith('SB')) return 33
        if (waypoint.extra.type === 'AD') return 23
        if (waypoint.extra.type === 'HP') return 13
      }

      // ADHP name partial
      if (isInfoInName && waypoint instanceof ADHP) {
        if (waypoint.extra.type === 'AD' && waypoint.code?.startsWith('SB')) return 32
        if (waypoint.extra.type === 'AD') return 22
        if (waypoint.extra.type === 'HP') return 12
      }

      // City exact name or partial match
      if (waypoint instanceof City && (normalizedName === normalizedInfo || isInfoInName)) return 11
      if (waypoint instanceof City && normalizedName?.startsWith(normalizedInfo)) return 10

      // AwWaypoint starts with name match
      if (waypoint instanceof AwWaypoint && normalizedName?.startsWith(normalizedInfo)) return 9

      // Custom name starts with
      if (normalizedCustomName?.startsWith(normalizedInfo)) return 7

      // Code starts with
      if (normalizedCode?.startsWith(normalizedInfo)) return 6

      // Name starts with
      if (normalizedName?.startsWith(normalizedInfo)) {
        // If ADHP starts with SB, higher score
        if (waypoint instanceof ADHP && waypoint.extra.type === 'AD' && normalizedCode?.startsWith('SB')) return 71
        return 5
      }

      return null
    }

    // Collect and score matches
    const scoredItems: { waypoint: Waypoint; score: number }[] = []
    let scoredFound = 0

    for (const waypoint of candidates) {
      const score = scoreWaypoint(waypoint)
      if (score !== null) {
        scoredItems.push({ waypoint, score })
        scoredFound++
        // Early stop if we found too many matches
        if (scoredFound >= earlyStopThreshold) break
      }
    }

    // Sort by score descending
    scoredItems.sort((a, b) => b.score - a.score)

    // Take the top maxResults
    let resultWaypoints = scoredItems.slice(0, maxResults).map((item) => item.waypoint)

    if (!adhpOnly && normalizedInfo.length >= 8 && !isRadialDistancePattern) {
      const searchGeocode = await this.geocodeApi.getGeocode(normalizedInfo)
      if (searchGeocode.isSuccess) {
        const geocodeResults = searchGeocode
          .getValue()
          .map((geo) =>
            CoordWaypoint.create({ coordinates: geo.coordinates, customName: geo.name }, { isAddress: true })
          )
        resultWaypoints = [...resultWaypoints, ...geocodeResults]
      }
    }

    return resultWaypoints
  }

  getAllADHPs(): Result<ADHP[]> {
    const ADHPs = this.ADHPs.map((adhp) => adhp.clone())

    if (ADHPs.length > 0) {
      return Result.ok(ADHPs)
    }

    return Result.fail('No ADHPs found')
  }

  getADHPData(code: string): ADHP {
    // O detalhe (ROTAER, INFOTEMP, METAR...) vem do LRU, alimentado pelo refresh por ICAO. Sem ele,
    // devolve o enxuto da lista residente — que ja tem nome, cidade, coordenadas e `rotaer`, o
    // suficiente para o cabecalho pintar imediatamente enquanto o detalhe carrega.
    const normalizedCode = code?.trim()?.toUpperCase()
    const cachedFull = normalizedCode ? this.fullADHPs.get(normalizedCode) : undefined

    if (cachedFull) {
      return cachedFull.clone()
    }

    const leanAdhp = this.ADHPs.find((wpt) => wpt.code === code)

    if (!leanAdhp) {
      return null
    }

    return leanAdhp.clone()
  }

  getUniqueWaypoint(code: string, name: string): Waypoint {
    return this.waypoints.find((wpt) => wpt.code === code && wpt.name === name)
  }
}
